import { useMemo } from 'react';
import { compareSsCode } from '../utils/ssCodeSort';

const FREQ = [
  { value: 'ANNUAL', label: 'Annual' },
  { value: 'SIX_MONTHLY', label: '6-monthly' },
  { value: 'QUARTERLY', label: 'Quarterly' },
  { value: 'MONTHLY', label: 'Monthly' },
];

function rowIncluded(row) {
  const v = row?.include_in_inspection;
  if (v === undefined || v === null) return true;
  return !(v === 0 || v === false || v === '0');
}

/**
 * Add/Edit Building — CS PDF extract preview.
 * Full / Partial above SS list; checkbox per row → include_in_inspection on save/apply.
 */
export default function CsApplyPreview({
  extract,
  onChangeRows,
  onChangeScope,
  seedChecklists,
  onSeedChecklistsChange,
  confirming,
  onConfirm,
  onDiscard,
  isEdit,
  csDbResult,
}) {
  const rows = Array.isArray(extract?.ss_instances) ? extract.ss_instances : [];
  const scope =
    String(extract?.inspection_scope || 'FULL').toUpperCase() === 'PARTIAL' ? 'PARTIAL' : 'FULL';

  const displayRows = useMemo(
    () =>
      rows
        .map((row, idx) => ({ row, idx }))
        .sort((a, b) => compareSsCode(a.row?.ss_code, b.row?.ss_code)),
    [rows]
  );

  const buildingBits = [
    extract?.building_name,
    extract?.address,
    extract?.compliance_schedule_number && `CS# ${extract.compliance_schedule_number}`,
    extract?.compliance_schedule_anniversary || extract?.cs_issue_date,
  ].filter(Boolean);
  const hasBuildingExtract = buildingBits.length > 0;
  if (!rows.length && !csDbResult && !hasBuildingExtract) return null;

  const updateRow = (idx, patch) => {
    const next = rows.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    onChangeRows(next);
  };

  const onCheckChange = (idx, checked) => {
    const next = rows.map((r, i) =>
      i === idx ? { ...r, include_in_inspection: checked ? 1 : 0 } : r
    );
    onChangeRows(next);

    if (scope === 'FULL' && !checked) {
      onChangeScope?.('PARTIAL');
      return;
    }
    if (scope === 'PARTIAL') {
      const allOn = next.length > 0 && next.every((r) => rowIncluded(r));
      if (allOn) onChangeScope?.('FULL');
    }
  };

  return (
    <div className="mt-3 space-y-3">
      {csDbResult && (
        <div className="p-3 bg-green-100 border border-green-300 rounded-lg">
          <p className="text-sm font-medium text-emerald-900">
            Applied: {csDbResult.ssTotal} SS · {csDbResult.unitsTotal} units
            {csDbResult.checklistsSeeded ? ` · ${csDbResult.checklistsSeeded} checklists` : ''}
          </p>
        </div>
      )}

      {!csDbResult && (rows.length > 0 || hasBuildingExtract) && (
        <div className="p-4 bg-amber-50 border border-amber-300 rounded-xl space-y-3">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h3 className="text-sm font-semibold text-amber-950">
                {rows.length
                  ? 'Extracted from PDF — review then Save / Apply'
                  : 'Building fields extracted — Save to write to DB'}
              </h3>
              {buildingBits.length > 0 && (
                <p className="text-xs text-amber-900 mt-1">{buildingBits.join(' · ')}</p>
              )}
              {!rows.length && (
                <p className="text-xs text-amber-800 mt-1">
                  No SS rows found in this PDF. Building/owner/dates can still be saved.
                </p>
              )}
            </div>
          </div>

          {rows.length > 0 && (
            <>
              <fieldset
                disabled
                className="rounded-lg border border-green-300 bg-green-100 px-3 py-2.5 flex flex-wrap gap-6"
              >
                <legend className="text-xs font-semibold text-amber-950 px-1">
                  CS coverage
                </legend>
                <label className="inline-flex items-center gap-2 text-sm text-amber-950">
                  <input type="radio" name="cs_insp_scope" checked={scope === 'FULL'} readOnly />
                  Full
                </label>
                <label className="inline-flex items-center gap-2 text-sm text-amber-950">
                  <input type="radio" name="cs_insp_scope" checked={scope === 'PARTIAL'} readOnly />
                  Partial
                </label>
                <p className="w-full text-[11px] text-amber-800 m-0">
                  Follows the Full / Partial choice under Owner. Unchecking any SS while Full still
                  switches this to Partial.
                </p>
              </fieldset>

              <div className="overflow-x-auto rounded-lg border border-green-300 bg-green-100 max-h-80 overflow-y-auto">
                <table className="min-w-full text-xs">
                  <thead className="bg-amber-100/80 text-amber-950 sticky top-0">
                    <tr>
                      <th className="text-left px-2 py-1.5 font-semibold w-10">In</th>
                      <th className="text-left px-2 py-1.5 font-semibold">SS</th>
                      <th className="text-left px-2 py-1.5 font-semibold">Location</th>
                      <th className="text-left px-2 py-1.5 font-semibold">Frequency</th>
                      <th className="text-left px-2 py-1.5 font-semibold">Units</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-amber-100">
                    {displayRows.map(({ row, idx }) => (
                      <tr key={`${row.ss_code}-${idx}`}>
                        <td className="px-2 py-1.5">
                          <input
                            type="checkbox"
                            checked={rowIncluded(row)}
                            onChange={(e) => onCheckChange(idx, e.target.checked)}
                            aria-label={`Include ${row.ss_code}`}
                            className="h-4 w-4 rounded border-gray-300 text-primary-600"
                          />
                        </td>
                        <td className="px-2 py-1.5 font-medium text-gray-900 whitespace-nowrap">
                          {row.ss_code}
                        </td>
                        <td className="px-2 py-1.5">
                          <input
                            className="w-full border rounded px-1.5 py-1"
                            value={row.location || row.name || ''}
                            onChange={(e) =>
                              updateRow(idx, { location: e.target.value, name: e.target.value })
                            }
                          />
                        </td>
                        <td className="px-2 py-1.5">
                          <select
                            className="w-full border rounded px-1.5 py-1"
                            value={row.inspection_frequency || 'ANNUAL'}
                            onChange={(e) =>
                              updateRow(idx, { inspection_frequency: e.target.value })
                            }
                          >
                            {FREQ.map((o) => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-2 py-1.5 w-20">
                          <input
                            type="number"
                            min={1}
                            max={200}
                            className="w-full border rounded px-1.5 py-1"
                            value={row.unit_count ?? 1}
                            onChange={(e) =>
                              updateRow(idx, {
                                unit_count: Math.max(1, Number(e.target.value) || 1),
                              })
                            }
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {rows.length > 0 && (
            <label className="flex items-start gap-2 text-sm text-amber-950">
              <input
                type="checkbox"
                className="mt-1"
                checked={seedChecklists}
                onChange={(e) => onSeedChecklistsChange(e.target.checked)}
              />
              <span>Seed empty unit checklists from SS defaults</span>
            </label>
          )}

          <div className="flex flex-wrap gap-2">
            {isEdit ? (
              <button
                type="button"
                disabled={confirming}
                onClick={onConfirm}
                className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold disabled:opacity-40"
              >
                {confirming ? 'Applying…' : 'Apply'}
              </button>
            ) : (
              <p className="text-xs text-amber-900 self-center">
                Choose Full or Partial under Owner, tick the SS in scope, then <strong>Save Building</strong> below.
              </p>
            )}
            <button
              type="button"
              disabled={confirming}
              onClick={onDiscard}
              className="px-3 py-2 rounded-lg border border-green-300 text-amber-900 text-sm bg-green-100"
            >
              Discard
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
